import changelog from '../CHANGELOG.json';
import { detectPhoneDevice } from '../utils/deviceLayout';

class VersionManager {
    constructor() {
        this.STORAGE_KEY = 'pi-schedule-last-version';
        this.currentVersion = this.getCurrentVersion();
    }

    /**
     * Check if the current device is a mobile device
     */
    isMobileDevice() {
        if (typeof window === 'undefined') return false;
        return detectPhoneDevice(window.innerWidth, window.innerHeight);
    }

    /**
     * Get the current version from package.json
     */
    getCurrentVersion() {
        const packageJson = require('../../package.json');
        return packageJson.version;
    }

    /**
     * Get the last version that was opened by the user
     */
    getLastOpenedVersion() {
        return localStorage.getItem(this.STORAGE_KEY);
    }

    /**
     * Save the current version as the last opened version
     */
    saveCurrentVersionAsLastOpened() {
        localStorage.setItem(this.STORAGE_KEY, this.currentVersion);
    }

    /**
     * Compare two version strings (semantic versioning)
     * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
     */
    compareVersions(v1, v2) {
        const normalize = (v) => v.split('.').map(Number);
        const a = normalize(v1);
        const b = normalize(v2);
        
        for (let i = 0; i < Math.max(a.length, b.length); i++) {
            const aVal = a[i] || 0;
            const bVal = b[i] || 0;
            
            if (aVal > bVal) return 1;
            if (aVal < bVal) return -1;
        }
        
        return 0;
    }

    /**
     * Check if the current version is higher than the last opened version
     */
    isVersionUpdated() {
        const lastVersion = this.getLastOpenedVersion();
        const currentVersion = this.getCurrentVersion();
        if(currentVersion == '0.2') {
            return true;
        }
        if (!lastVersion) {
            // First time opening the app
            this.markCurrentVersionAsSeen();
            return false;
        }
        
        return this.compareVersions(this.currentVersion, lastVersion) > 0;
    }

    /**
     * Get all changes since the last opened version
     */
    getChangesSinceLastVersion() {
        var lastVersion = this.getLastOpenedVersion();
        
        if (!lastVersion) {
            lastVersion = '0.1';
        }

        const changes = [];
        
        // Find all versions newer than the last opened version
        for (const versionData of changelog.versions) {
            if (this.compareVersions(versionData.version, lastVersion) > 0 && 
                this.compareVersions(versionData.version, this.currentVersion) <= 0) {
                changes.push(versionData);
            }
        }
        
        // Sort by version (newest first)
        changes.sort((a, b) => this.compareVersions(b.version, a.version));
        
        return changes;
    }

    /**
     * Get all changes for a specific version
     */
    getChangesForVersion(version) {
        const versionData = changelog.versions.find(v => v.version === version);
        return versionData ? versionData.changes : [];
    }

    /**
     * Get all available versions
     */
    getAllVersions() {
        return changelog.versions.sort((a, b) => this.compareVersions(b.version, a.version));
    }

    /**
     * Mark the current version as seen by the user
     */
    markCurrentVersionAsSeen() {
        this.saveCurrentVersionAsLastOpened();
    }

    /**
     * Check if we should show the changelog modal
     */
    shouldShowChangelog() {
        // Don't show changelog on mobile devices
        if (this.isMobileDevice()) {
            return false;
        }
        
        return this.isVersionUpdated() && this.getChangesSinceLastVersion().length > 0;
    }
}

export default new VersionManager();